/* Super Upsell - Shrine Pro cart drawer block
 * Handles: variant switching (price, compare price, badge, image, button state),
 * scroll-snap slider (arrows, dots, loop, keyboard, autoplay) and session close.
 * Add to cart itself is handled by the theme's <product-form data-is-cart-upsell="true">,
 * which re-renders the whole cart drawer (including this block) after adding.
 */
(() => {
  if (customElements.get('super-upsell')) return;

  const formatMoney = (cents, format) => {
    const fmt = format || '€{{amount_with_comma_separator}}';
    const re = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(re);
    const key = match ? match[1] : 'amount';
    const value = Number(cents) || 0;

    const build = (decimals, thousands, decimal) => {
      const [int, dec] = (value / 100).toFixed(decimals).split('.');
      const withThousands = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? withThousands + decimal + dec : withThousands;
    };

    let out;
    switch (key) {
      case 'amount_no_decimals': out = build(0, ',', '.'); break;
      case 'amount_with_comma_separator': out = build(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = build(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = build(2, "'", '.'); break;
      case 'amount_with_space_separator': out = build(2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': out = build(0, ' ', ','); break;
      default: out = build(2, ',', '.');
    }
    return fmt.replace(re, out);
  };

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inEditor = () => Boolean(window.Shopify && window.Shopify.designMode);

  class SuperUpsell extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.storageKey = `super-upsell-closed-${this.dataset.blockId}`;
      if (this.dataset.closable === 'true' && !inEditor() && this.isClosed()) {
        this.hidden = true;
        return;
      }

      this.moneyFormat = this.dataset.moneyFormat;
      this.badgeTemplate = this.dataset.badgeTemplate || '';
      this.track = this.querySelector('[data-su-track]');
      this.slides = Array.from(this.querySelectorAll('[data-su-slide]'));
      this.prevBtn = this.querySelector('[data-su-prev]');
      this.nextBtn = this.querySelector('[data-su-next]');
      this.dotsEl = this.querySelector('[data-su-dots]');
      this.loop = this.dataset.loop === 'true';

      const closeBtn = this.querySelector('[data-su-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());

      this.querySelectorAll('[data-su-card]').forEach((card) => this.initCard(card));

      if (this.track && this.slides.length > 1) this.initSlider();
    }

    disconnectedCallback() {
      this.clearAutoplay();
      this._ready = false;
    }

    /* ---------- Close ---------- */
    isClosed() {
      try { return sessionStorage.getItem(this.storageKey) === '1'; } catch (e) { return false; }
    }

    close() {
      this.hidden = true;
      this.clearAutoplay();
      try { sessionStorage.setItem(this.storageKey, '1'); } catch (e) { /* storage unavailable */ }
    }

    /* ---------- Variants ---------- */
    initCard(card) {
      const json = card.querySelector('[data-su-variants]');
      if (!json) return;
      let variants;
      try { variants = JSON.parse(json.textContent); } catch (e) { return; }

      const apply = (id) => {
        const variant = variants.find((v) => String(v.id) === String(id));
        if (variant) this.renderVariant(card, variant);
      };

      const select = card.querySelector('[data-su-select]');
      if (select) select.addEventListener('change', (e) => apply(e.target.value));

      const pills = card.querySelectorAll('[data-su-pill]');
      pills.forEach((pill) => {
        pill.addEventListener('click', () => {
          pills.forEach((p) => p.setAttribute('aria-checked', String(p === pill)));
          apply(pill.dataset.variantId);
        });
      });
    }

    renderVariant(card, v) {
      const input = card.querySelector('[data-su-id]');
      if (input) {
        input.value = v.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const price = card.querySelector('[data-su-price]');
      if (price) price.textContent = formatMoney(v.price, this.moneyFormat);

      const hasCompare = v.compare > v.price;

      const compare = card.querySelector('[data-su-compare]');
      if (compare) {
        compare.hidden = !hasCompare;
        const value = compare.querySelector('[data-su-compare-value]');
        if (value) value.textContent = hasCompare ? formatMoney(v.compare, this.moneyFormat) : '';
      }

      card.querySelectorAll('[data-su-badge]').forEach((badge) => {
        badge.hidden = !hasCompare;
        if (!hasCompare) return;
        const saved = v.compare - v.price;
        const percent = Math.round((saved * 100) / v.compare);
        badge.textContent = this.badgeTemplate
          .replace(/\[percent\]/g, percent)
          .replace(/\[amount\]/g, formatMoney(saved, this.moneyFormat));
      });

      const img = card.querySelector('[data-su-image]');
      if (img && v.image) {
        img.removeAttribute('srcset');
        img.src = v.image;
      }

      const btn = card.querySelector('[data-su-add]');
      if (btn) {
        btn.disabled = !v.available;
        const label = btn.querySelector('[data-su-add-label]');
        if (label) label.textContent = v.available ? btn.dataset.labelAdd : btn.dataset.labelSold;
      }
    }

    /* ---------- Slider ---------- */
    initSlider() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.stopAutoplay();
          this.goTo(this.currentIndex() - 1);
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.stopAutoplay();
          this.goTo(this.currentIndex() + 1);
        });
      }

      if (this.dotsEl) {
        this.dotsEl.innerHTML = '';
        this.dots = this.slides.map((_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'su__dot';
          dot.setAttribute('aria-label', `${i + 1} / ${this.slides.length}`);
          dot.addEventListener('click', () => {
            this.stopAutoplay();
            this.goTo(i);
          });
          this.dotsEl.appendChild(dot);
          return dot;
        });
      }

      let frame;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.updateUI());
      }, { passive: true });

      this.track.addEventListener('keydown', (e) => {
        if (e.target !== this.track) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); this.stopAutoplay(); this.goTo(this.currentIndex() + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.stopAutoplay(); this.goTo(this.currentIndex() - 1); }
      });

      this.updateUI();

      if (this.dataset.autoplay === 'true' && !reducedMotion()) {
        this.autoplayStopped = false;
        this.paused = false;
        this.startAutoplay();
        const pause = () => { this.paused = true; };
        const resume = () => { this.paused = false; };
        this.addEventListener('mouseenter', pause);
        this.addEventListener('mouseleave', resume);
        this.addEventListener('focusin', pause);
        this.addEventListener('focusout', resume);
        this.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
      }
    }

    currentIndex() {
      const left = this.track.scrollLeft;
      let index = 0;
      let min = Infinity;
      this.slides.forEach((slide, i) => {
        const distance = Math.abs(slide.offsetLeft - left);
        if (distance < min) { min = distance; index = i; }
      });
      return index;
    }

    atStart() { return this.track.scrollLeft <= 2; }

    atEnd() { return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2; }

    goTo(target) {
      const count = this.slides.length;
      const forward = target > this.currentIndex();
      const backward = target < this.currentIndex();

      if ((forward && this.atEnd()) || target >= count) target = this.loop ? 0 : count - 1;
      else if ((backward && this.atStart()) || target < 0) target = this.loop ? count - 1 : 0;

      this.track.scrollTo({
        left: this.slides[target].offsetLeft,
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    }

    updateUI() {
      const index = this.atEnd() ? this.slides.length - 1 : this.currentIndex();
      if (this.dots) {
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
      }
      if (!this.loop) {
        if (this.prevBtn) this.prevBtn.disabled = this.atStart();
        if (this.nextBtn) this.nextBtn.disabled = this.atEnd();
      }
    }

    startAutoplay() {
      this.clearAutoplay();
      const speed = Math.max(3, Number(this.dataset.speed) || 5) * 1000;
      this.timer = setInterval(() => {
        if (this.autoplayStopped || this.paused || document.visibilityState !== 'visible') return;
        if (!this.closest('.drawer.active') && !inEditor() && this.closest('cart-drawer')) return;
        this.goTo(this.currentIndex() + 1);
      }, speed);
    }

    stopAutoplay() {
      this.autoplayStopped = true;
      this.clearAutoplay();
    }

    clearAutoplay() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('super-upsell', SuperUpsell);
})();
